'use client';

import { useCallback, useState } from 'react';
import { FieldValues, useForm, UseFormProps, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

// `any`, and it stays `any` until someone types it properly. `unknown` was tried and reverted:
// this alias is consumed inside this very file - yup.object().shape(schema),
// Object.entries(schema.fields), yupResolver(schema) - so widening it to `unknown` produces ten
// type errors here and fails `next build` in all four apps, which run with
// `ignoreBuildErrors: false`. The honest fix is to give the schema its real yup type, which
// reaches every caller of useCustomForm and is not this ticket. See TD-031 in
// docs/technical-debt.md, tracked in #2162.
type useCustomFormSchema<TFieldValues> = any;

type UseCustomFormReturn<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
> = UseFormReturn<TFieldValues, TContext> & {
  /**
   * The schema the resolver is currently built from. updateSchema sets React state, so a
   * new schema is only live from the next render; depending on this value is how a caller
   * knows the new rules are actually the ones being validated against.
   */
  schema: useCustomFormSchema<TFieldValues>;
  updateSchema: (
    newSchema: useCustomFormSchema<TFieldValues>,
    {
      ignoreSchema,
      resetSchema,
    }?: { ignoreSchema?: useCustomFormSchema<TFieldValues>; resetSchema?: boolean },
  ) => void;
};

export const useCustomForm = <TFieldValues extends FieldValues = FieldValues, TContext = any>(
  props?: Omit<UseFormProps<TFieldValues, TContext>, 'resolver'> & {
    schema?: useCustomFormSchema<TFieldValues>;
  },
): UseCustomFormReturn<TFieldValues> => {
  const [schema, setSchema] = useState<useCustomFormSchema<TFieldValues> | null>(
    yup.object().shape(props?.schema) ?? null,
  );

  const updateSchema = useCallback(
    (
      newSchema: useCustomFormSchema<TFieldValues>,
      {
        ignoreSchema = {},
        resetSchema = false,
      }: {
        ignoreSchema?: useCustomFormSchema<TFieldValues>;
        resetSchema?: boolean;
      } = {
        ignoreSchema: {},
        resetSchema: false,
      },
    ) => {
      if (resetSchema) {
        setSchema(yup.object().shape(newSchema));
      } else {
        setSchema((oldSchema: useCustomFormSchema<TFieldValues>) => {
          if (oldSchema) {
            const excludeIgnoredSchema: useCustomFormSchema<TFieldValues> = {};
            for (const [name, value] of Object.entries(oldSchema.fields)) {
              if (ignoreSchema && !(name in ignoreSchema)) {
                excludeIgnoredSchema[name] = value;
              }
            }

            return yup.object().shape({
              ...excludeIgnoredSchema,
              ...newSchema,
            }) as useCustomFormSchema<TFieldValues>;
          }
          return yup.object().shape(newSchema);
        });
      }
    },
    [],
  );

  const form = useForm<TFieldValues>({
    ...props,
    resolver: schema ? yupResolver<useCustomFormSchema<TFieldValues>>(schema) : undefined,
    mode: 'onChange',
  });

  return {
    ...form,
    schema,
    updateSchema,
  };
};
